import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import { clearErrors, deleteCategory, getCategories, deleteCategories } from '../../actions/categoryAction';
import {
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    IconButton,
    Collapse,
    Box,
    Tooltip
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Info as InfoIcon } from '@mui/icons-material';
import { DELETE_CATEGORY_RESET, DELETE_CATEGORIES_RESET } from '../../constants/categoryConstants';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

const CategoryCard = (props) => {
    const { category, deleteCategoryHandler } = props;
    const [expanded, setExpanded] = useState(false);
    const theme = useTheme();

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const glowColor = theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.light;

    return (
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Card sx={{
                background: `rgba(255, 255, 255, ${theme.palette.mode === 'dark' ? '0.05' : '0.4'})`,
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: `0 8px 32px 0 ${glowColor}`,
                color: theme.palette.text.primary,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <div className="flex flex-col items-center text-center">
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            transition={{ type: "spring", stiffness: 400, damping: 10 }}
                            className="w-24 h-24 rounded-full overflow-hidden mb-4 border-2"
                            style={{ borderColor: glowColor }}
                        >
                            <img draggable="false" src={category.logo.url} alt={category.name} className="w-full h-full object-cover" />
                        </motion.div>
                        <Typography variant="h5" component="div" className="font-semibold">
                            {category.name}
                        </Typography>
                        <Tooltip title={category._id}>
                            <InfoIcon sx={{ fontSize: 16, color: 'gray', mt: 1 }} />
                        </Tooltip>
                    </div>
                </CardContent>
                <CardActions disableSpacing sx={{ justifyContent: 'space-between', p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Actions editRoute={"category"} deleteHandler={deleteCategoryHandler} id={category._id} />
                    <IconButton
                        onClick={handleExpandClick}
                        aria-expanded={expanded}
                        aria-label="show more"
                        sx={{
                            transform: !expanded ? 'rotate(0deg)' : 'rotate(180deg)',
                            transition: theme.transitions.create('transform', {
                                duration: theme.transitions.duration.shortest,
                            }),
                        }}
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                </CardActions>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <CardContent sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="h6" className="mb-2 text-center font-semibold">Sub Categories</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {category.children.map((subCategory, index) => (
                                <motion.div
                                    key={subCategory._id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-center justify-between p-2 rounded-lg"
                                    style={{ background: `rgba(255, 255, 255, ${theme.palette.mode === 'dark' ? '0.05' : '0.1'})` }}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full overflow-hidden">
                                            <img draggable="false" src={subCategory.logo.url} alt={subCategory.name} className="w-full h-full object-cover" />
                                        </div>
                                        <Typography variant="body1">{subCategory.name}</Typography>
                                        <Tooltip title={subCategory._id}>
                                            <InfoIcon sx={{ fontSize: 14, color: 'gray' }} />
                                        </Tooltip>
                                    </div>
                                    <Actions editRoute={"category"} deleteHandler={deleteCategoryHandler} id={subCategory._id} />
                                </motion.div>
                            ))}
                        </Box>
                    </CardContent>
                </Collapse>
            </Card>
        </motion.div>
    );
}


const CategoryTable = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();

    const { categories, error } = useSelector((state) => state.categories);
    const { loading, isDeleted, error: deleteError, isCategoriesDeleted, error: deleteCategoriesError } = useSelector((state) => state.deleteCategory);

    const [categorySelection, setCategorySelection] = useState([]);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (deleteError) {
            enqueueSnackbar(deleteError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isDeleted) {
            enqueueSnackbar("Category Deleted Successfully", { variant: "success" });
            dispatch({ type: DELETE_CATEGORY_RESET });
        }
        if (deleteCategoriesError) {
            enqueueSnackbar(deleteCategoriesError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isCategoriesDeleted) {
            enqueueSnackbar("Categories Deleted Successfully", { variant: "success" });
            dispatch({ type: DELETE_CATEGORIES_RESET });
            setCategorySelection([]);
        }
        dispatch(getCategories());
    }, [dispatch, error, deleteError, isDeleted, enqueueSnackbar, isCategoriesDeleted, deleteCategoriesError]);

    const deleteCategoryHandler = (id) => {
        dispatch(deleteCategory(id));
    }

    const deleteCategoriesHandler = (ids) => {
        dispatch(deleteCategories(ids));
    }

    return (
        <>
            <MetaData title="Admin Categories | Flipkart" />

            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Categories</h1>
                <div className="flex gap-4 items-center">
                    {categorySelection.length > 0 && (
                        <Button
                            onClick={() => deleteCategoriesHandler(categorySelection)}
                            variant="contained"
                            color="error"
                        >
                            Delete Selected ({categorySelection.length})
                        </Button>
                    )}
                    <Button
                        component={Link}
                        to="/admin/new_category"
                        variant="contained"
                        color="primary"
                    >
                        New Category
                    </Button>
                </div>
            </div>
            <Grid container spacing={4}>
                {categories && categories.map((category) => (
                    <Grid item xs={12} sm={6} md={4} key={category._id}>
                        <CategoryCard
                            category={category}
                            deleteCategoryHandler={deleteCategoryHandler}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
    );
};

export default CategoryTable;